import { cloneElement } from "react";
import { Stack } from "@mui/system";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import PauseCircleOutlinedIcon from "@mui/icons-material/PauseCircleOutlined";

type StatusType =
  | "succeeded"
  | "failed"
  | "running"
  | "paused"
  | "active"
  | "inactive";

export const STATUS_CONFIG: Record<
  StatusType,
  {
    label: string;
    color: string;
    bg: string;
    border: string;
    icon: JSX.Element;
  }
> = {
  succeeded: {
    label: "Succeeded",
    color: "#1E4620",
    bg: "#EDF7ED",
    border: "#2E7D3280",
    icon: <CheckCircleOutlinedIcon fontSize="small" />,
  },
  failed: {
    label: "Failed",
    color: "#5F2120",
    bg: "#FDEDED",
    border: "#D32F2F80",
    icon: <CancelOutlinedIcon fontSize="small" />,
  },
  running: {
    label: "Running",
    color: "#7B1FA2",
    bg: "#9C27B01F",
    border: "#9C27B080",
    icon: <AutorenewIcon fontSize="small" />,
  },
  paused: {
    label: "Paused",
    color: "#00000099",
    bg: "#0000001F",
    border: "#9E9E9E",
    icon: <PauseCircleOutlinedIcon fontSize="small" />,
  },
  active: {
    label: "Active",
    color: "#1E4620",
    bg: "#EDF7ED",
    border: "#2E7D3280",
    icon: <CheckCircleOutlinedIcon fontSize="small" />,
  },
  inactive: {
    label: "Inactive",
    color: "#5F2120",
    bg: "#FDEDED",
    border: "#D32F2F80",
    icon: <CancelOutlinedIcon fontSize="small" />,
  },
};

// `size="small"` is the compact variant used inside the Brand Configuration
// Overview header cards, whose Figma spec is a 10px/500 label with a 14px
// line height so the whole card fits its 72px height. Every other call site
// (the See Last 10 Runs table, the brand title chip) keeps the original 13px
// default by simply not passing the prop.
const StatusChip = ({
  status,
  size = "medium",
}: {
  status?: string;
  size?: "small" | "medium";
}) => {
  const key = status?.toLowerCase();

  if (!key || !(key in STATUS_CONFIG)) {
    return "";
  }

  const config = STATUS_CONFIG[key as StatusType];
  const isSmall = size === "small";

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.5}
      sx={{
        px: 0.5,
        py: isSmall ? 0 : 0.3,
        borderRadius: "4px",
        backgroundColor: config.bg,
        border: `1px solid ${config.border}`,
        width: "fit-content",
      }}
    >
      <span
        style={{
          display: "flex",
          color: config.color,
          ...(isSmall ? { fontSize: "14px" } : {}),
        }}
      >
        {isSmall
          ? cloneElement(config.icon, { fontSize: "inherit" })
          : config.icon}
      </span>
      <span
        style={{
          fontSize: isSmall ? "10px" : "13px",
          lineHeight: isSmall ? "14px" : undefined,
          fontWeight: 500,
          color: config.color,
        }}
      >
        {config.label}
      </span>
    </Stack>
  );
};

export default StatusChip;
